import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import api from '../utils/api';
import PageHeader from '../components/PageHeader';
import { Mic, RefreshCw, Check, X, RotateCcw, ChevronDown, ChevronUp, ExternalLink, Trash2, Eye, Users, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import RichEditor from '../components/RichEditor';
import { markdownToHtml, looksLikeMarkdown } from '../utils/markdownUtils';

const STATUS_LABEL = {
  pending:  { text: '대기중', cls: 'bg-yellow-500/20 text-yellow-400' },
  approved: { text: '승인됨', cls: 'bg-green-500/20 text-green-400' },
  rejected: { text: '거절됨', cls: 'bg-gray-700 text-gray-500' },
};

// "Speaker 1", "Speaker 2" 등 감지
function extractSpeakers(text) {
  if (!text) return [];
  const set = new Set();
  const re = /\bSpeaker\s*\d+/gi;
  let m;
  while ((m = re.exec(text)) !== null) {
    const normalized = 'Speaker ' + m[0].match(/\d+/)[0];
    set.add(normalized);
  }
  return [...set].sort((a, b) => parseInt(a.match(/\d+/)[0]) - parseInt(b.match(/\d+/)[0]));
}

function applyReplacements(text, mappings) {
  let result = text;
  for (const [speaker, name] of Object.entries(mappings)) {
    if (name.trim()) {
      result = result.replace(new RegExp(`\\b${speaker.replace(' ', '\\s*')}\\b`, 'gi'), name.trim());
    }
  }
  return result;
}

function toHtml(src) {
  if (!src) return '';
  return looksLikeMarkdown(src) ? markdownToHtml(src) : src;
}

function formatDuration(raw) {
  if (!raw) return null;
  const secs = parseInt(raw);
  if (!isNaN(secs) && secs > 0) {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    if (h > 0) return `${h}시간 ${m}분`;
    if (m > 0) return `${m}분${s > 0 ? ` ${s}초` : ''}`;
    return `${s}초`;
  }
  return raw;
}

// ─── 미리보기/편집 모달 ──────────────────────────────────────────
const DRAFT_KEY = (id) => `plaud_draft_${id}`;

function PreviewModal({ rec, onClose, onSaved }) {
  const rawSrc = rec.summary || rec.transcript || '';
  const speakers = extractSpeakers(rawSrc);

  const draftKey = DRAFT_KEY(rec.id);
  const savedDraft = localStorage.getItem(draftKey);

  const [mappings, setMappings] = useState(() =>
    Object.fromEntries(speakers.map(s => [s, '']))
  );
  const [merges, setMerges]           = useState({});
  const [editorContent, setEditorContent] = useState(() => savedDraft || toHtml(rawSrc));
  const [editorKey, setEditorKey]   = useState(0);
  const [title, setTitle]           = useState(rec.title || '');
  const [saving, setSaving]         = useState(false);
  const [drafting, setDrafting]     = useState(false);
  const [draftMsg, setDraftMsg]     = useState(savedDraft ? '이전 임시저장 내용을 불러왔습니다.' : '');
  const [error, setError]           = useState('');
  const htmlRef = useRef(editorContent);

  const autoSaveTimer = useRef(null);

  const handleEditorChange = html => {
    const text = html?.replace(/<[^>]*>/g, '').trim() ?? '';
    if (!text) return;
    htmlRef.current = html;
    // 에디터 변경 시 1.5초 후 자동 임시저장
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      localStorage.setItem(draftKey, html);
      setDraftMsg('자동 임시저장됨');
      setTimeout(() => setDraftMsg(''), 1500);
    }, 1500);
  };

  const saveDraft = () => {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    const content = htmlRef.current;
    if (!content) return;
    localStorage.setItem(draftKey, content);
    setDraftMsg('임시저장 완료');
    setTimeout(() => setDraftMsg(''), 2000);
  };

  const applyMappings = () => {
    // 합쳐진 Speaker는 target의 이름을 사용
    const finalMappings = {};
    for (const sp of speakers) {
      const targetSp = merges[sp] || sp;
      const name = mappings[targetSp]?.trim();
      if (name) finalMappings[sp] = name;
    }
    const replaced = applyReplacements(rawSrc, finalMappings);
    const newHtml  = toHtml(replaced);
    htmlRef.current = newHtml;
    setEditorContent(newHtml);
    setEditorKey(k => k + 1);
  };

  const setMerge = (sp, target) => {
    setMerges(m => {
      const next = { ...m };
      if (target) next[sp] = target; else delete next[sp];
      return next;
    });
  };

  const handleSave = async () => {
    setError('');
    if (!title.trim()) { setError('제목을 입력해주세요.'); return; }
    const content = htmlRef.current;
    const textContent = content?.replace(/<[^>]*>/g, '').trim() ?? '';
    if (!textContent) { setError('내용이 없습니다.'); return; }
    setSaving(true);
    try {
      const r = await api.post(`/plaud/${rec.id}/approve`, { title: title.trim(), content });
      onSaved(r.data.meetingNoteId);
    } catch (e) {
      setError(e.response?.data?.message || '저장 실패');
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-6">
      {/* 배경 */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* 모달 박스 */}
      <div
        className="relative bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-3xl flex flex-col"
        style={{ maxHeight: 'calc(100vh - 48px)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* 헤더: 제목 입력 */}
        <div className="px-5 pt-5 pb-4 border-b border-gray-800 flex-shrink-0">
          <div className="flex items-center gap-3">
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="회의록 제목"
              className="flex-1 bg-gray-800 border border-gray-700 focus:border-brand rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none transition-colors"
            />
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-800 text-gray-500 flex-shrink-0">
              <X size={14} />
            </button>
          </div>
          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
            {rec.recordedAt && <span>🕐 {dayjs(rec.recordedAt).format('YYYY-MM-DD HH:mm')}</span>}
            {rec.duration  && <span>⏱ {formatDuration(rec.duration)}</span>}
            <span className="text-gray-700">Plaud 요약 기반</span>
          </div>
        </div>

        {/* 참석자 이름 치환 */}
        {speakers.length > 0 && (
          <div className="px-5 py-3 border-b border-gray-800 flex-shrink-0">
            <div className="flex items-center gap-2 mb-2">
              <Users size={12} className="text-gray-500" />
              <span className="text-xs font-medium text-gray-400">참석자 이름 치환</span>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-2 items-center">
              {speakers.map(sp => {
                const mergedInto = merges[sp];
                return (
                  <div key={sp} className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-500 whitespace-nowrap">{sp}</span>
                    <span className="text-gray-600 text-xs">→</span>

                    {mergedInto ? (
                      /* 합쳐진 상태: 칩으로 표시 */
                      <div className="flex items-center gap-1 px-2 py-1 bg-gray-800 border border-gray-700 rounded-lg">
                        <span className="text-xs text-gray-300">{mergedInto}</span>
                        <span className="text-xs text-gray-500">와 합침</span>
                        <button
                          onClick={() => setMerge(sp, null)}
                          className="ml-0.5 text-gray-500 hover:text-gray-300"
                        >
                          <X size={9} />
                        </button>
                      </div>
                    ) : (
                      /* 일반 상태: 이름 입력 + 합치기 선택 */
                      <>
                        <input
                          value={mappings[sp] || ''}
                          onChange={e => setMappings(m => ({ ...m, [sp]: e.target.value }))}
                          onKeyDown={e => e.key === 'Enter' && applyMappings()}
                          placeholder="이름"
                          className="w-24 px-2 py-1 text-xs bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-brand transition-colors"
                        />
                        <select
                          value=""
                          onChange={e => { if (e.target.value) setMerge(sp, e.target.value); }}
                          className="px-2 py-1 text-xs bg-gray-800 border border-gray-700 rounded-lg text-gray-500 focus:outline-none focus:border-brand transition-colors cursor-pointer"
                        >
                          <option value="">합치기</option>
                          {speakers.filter(s => s !== sp && !merges[s]).map(s => (
                            <option key={s} value={s}>
                              {s}{mappings[s] ? ` (${mappings[s]})` : ''}
                            </option>
                          ))}
                        </select>
                      </>
                    )}
                  </div>
                );
              })}
              <button
                onClick={applyMappings}
                className="px-3 py-1 text-xs rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-200 transition-colors"
              >
                적용
              </button>
            </div>
          </div>
        )}

        {/* 에디터 (스크롤) */}
        <div className="flex-1 overflow-y-auto border-b border-gray-800 min-h-0">
          <RichEditor
            key={editorKey}
            content={editorContent}
            onChange={handleEditorChange}
          />
        </div>

        {/* 푸터 */}
        <div className="flex items-center justify-between px-5 py-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={saveDraft}
              disabled={drafting}
              className="px-3 py-1.5 text-xs rounded-lg border border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-600 transition-colors"
            >
              임시저장
            </button>
            <p className={`text-xs ${error ? 'text-red-400' : draftMsg ? 'text-green-500' : 'text-gray-700'}`}>
              {error || draftMsg || '수정 후 승인하면 회의록에 등록됩니다'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:bg-gray-800 transition-colors"
            >
              취소
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand hover:bg-brand/80 text-white text-sm font-medium transition-colors disabled:opacity-50"
            >
              {saving ? <RefreshCw size={13} className="animate-spin" /> : <Check size={13} />}
              {saving ? '저장 중...' : '승인 & 회의록 저장'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── 녹음 카드 ────────────────────────────────────────────────────
function RecordingCard({ rec, onPreview, onReject, onReset, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const status = STATUS_LABEL[rec.status] || STATUS_LABEL.pending;

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
      <div className="p-4 flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-brand/10 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Mic size={16} className="text-brand" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-white font-medium text-sm truncate">{rec.title}</p>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${status.cls}`}>{status.text}</span>
          </div>
          <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
            {rec.recordedAt && <span>{dayjs(rec.recordedAt).format('YYYY-MM-DD HH:mm')}</span>}
            {rec.duration && <span>· {formatDuration(rec.duration)}</span>}
            {rec.approver && <span>· {rec.approver.name} 승인</span>}
          </div>
          {rec.summary && (
            <p className="text-gray-400 text-xs mt-2 leading-relaxed line-clamp-2">{rec.summary}</p>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {rec.status === 'approved' && rec.meetingNoteId && (
            <button
              onClick={() => navigate(`/work/meeting/${rec.meetingNoteId}`)}
              className="p-1.5 text-gray-500 hover:text-brand rounded-lg hover:bg-gray-800"
              title="회의록 보기"
            >
              <ExternalLink size={14} />
            </button>
          )}
          {(rec.status === 'pending' || rec.status === 'approved') && (
            <button
              onClick={() => onPreview(rec)}
              className="flex items-center gap-1 px-2.5 py-1.5 bg-brand/20 hover:bg-brand/30 text-brand rounded-lg text-xs font-medium"
            >
              <Eye size={12} /> 미리보기
            </button>
          )}
          {rec.status === 'pending' && (
            <button
              onClick={() => onReject(rec.id)}
              className="p-1.5 text-gray-500 hover:text-red-400 rounded-lg hover:bg-gray-800"
              title="거절"
            >
              <X size={14} />
            </button>
          )}
          {rec.status !== 'pending' && (
            <button
              onClick={() => onReset(rec.id)}
              className="p-1.5 text-gray-500 hover:text-gray-300 rounded-lg hover:bg-gray-800"
              title="대기 상태로 되돌리기"
            >
              <RotateCcw size={14} />
            </button>
          )}
          <button
            onClick={() => onDelete(rec.id)}
            className="p-1.5 text-gray-600 hover:text-red-400 rounded-lg hover:bg-gray-800"
            title="삭제"
          >
            <Trash2 size={14} />
          </button>
          <button
            onClick={() => setExpanded(v => !v)}
            className="p-1.5 text-gray-600 hover:text-gray-400 rounded-lg hover:bg-gray-800"
          >
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
      </div>

      {expanded && rec.transcript && (
        <div className="border-t border-gray-800 p-4">
          <p className="text-gray-500 text-xs font-medium mb-2 uppercase tracking-wide">전사본 (원본)</p>
          <div className="bg-gray-950 rounded-lg p-3 max-h-64 overflow-y-auto">
            <p className="text-gray-300 text-xs leading-relaxed whitespace-pre-wrap">{rec.transcript}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── 페이지 ───────────────────────────────────────────────────────
/* ── 붙여넣기 → AI 문서화 모달 ─────────────────────────────────────── */
function ComposeModal({ onClose, onSaved }) {
  const [text, setText]       = useState('');
  const [hintTitle, setTitle] = useState('');
  const [html, setHtml]       = useState('');
  const [docTitle, setDocTitle] = useState('');
  const [busy, setBusy]       = useState(false);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  const convert = async () => {
    if (!text.trim()) { setError('내용을 붙여넣어 주세요.'); return; }
    setBusy(true); setError('');
    try {
      const r = await api.post('/plaud/compose', { text, title: hintTitle });
      setHtml(r.data.html);
      setDocTitle(r.data.title);
    } catch (e) {
      setError(e.response?.data?.message || '변환에 실패했습니다.');
    } finally { setBusy(false); }
  };

  const save = async () => {
    setSaving(true); setError('');
    try {
      const r = await api.post('/plaud/compose/save', { html, title: docTitle });
      onSaved(r.data.meetingNoteId);
    } catch (e) {
      setError(e.response?.data?.message || '저장에 실패했습니다.');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-5xl h-[88vh] flex flex-col"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800 shrink-0">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-brand" />
            <h3 className="font-bold text-gray-100">붙여넣기로 회의록 만들기</h3>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300"><X size={18} /></button>
        </div>

        {!html ? (
          <div className="flex-1 min-h-0 flex flex-col p-5 gap-3">
            <input
              value={hintTitle}
              onChange={e => setTitle(e.target.value)}
              placeholder="회의 제목 (선택 — 비워두면 AI가 지어냅니다)"
              className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white caret-white focus:outline-none focus:border-brand placeholder-gray-500 shrink-0"
            />
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder="Plaud가 정리해준 요약본을 여기에 붙여넣으세요."
              className="flex-1 min-h-0 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white caret-white focus:outline-none focus:border-brand placeholder-gray-500 resize-none leading-relaxed"
            />
            <div className="flex items-center justify-between shrink-0">
              <span className="text-xs text-gray-500">{text.length.toLocaleString()}자</span>
              {error && <span className="text-xs text-red-400">{error}</span>}
              <button onClick={convert} disabled={busy || !text.trim()}
                className="flex items-center gap-2 bg-brand text-white rounded-lg px-5 py-2 text-sm font-bold hover:bg-brand/80 disabled:opacity-50">
                {busy
                  ? <><RefreshCw size={14} className="animate-spin" /> 문서로 정리하는 중...</>
                  : <><Sparkles size={14} /> 문서로 만들기</>}
              </button>
            </div>
            {busy && <p className="text-xs text-gray-500 text-center shrink-0">내용이 길면 1분 이상 걸릴 수 있습니다.</p>}
          </div>
        ) : (
          <>
            <div className="px-5 pt-4 shrink-0">
              <input
                value={docTitle}
                onChange={e => setDocTitle(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm font-bold text-white caret-white focus:outline-none focus:border-brand"
              />
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto m-5 mt-3 rounded-xl bg-white p-8">
              <div className="plaud-doc" dangerouslySetInnerHTML={{ __html: html }} />
            </div>
            <div className="flex items-center justify-between px-5 pb-5 shrink-0 gap-3">
              <button onClick={() => { setHtml(''); setError(''); }}
                className="text-sm text-gray-400 hover:text-gray-200">← 다시 만들기</button>
              {error && <span className="text-xs text-red-400">{error}</span>}
              <button onClick={save} disabled={saving}
                className="bg-brand text-white rounded-lg px-5 py-2 text-sm font-bold hover:bg-brand/80 disabled:opacity-50">
                {saving ? '저장 중...' : '회의록으로 저장'}
              </button>
            </div>
          </>
        )}

        <style>{`
          .plaud-doc { color:#0f172a; font-size:14px; line-height:1.7; }
          .plaud-doc h1 { font-size:24px; font-weight:800; margin:0 0 18px; color:#0f172a; }
          .plaud-doc h2 { font-size:17px; font-weight:700; margin:26px 0 10px; color:#0f172a;
                          border-bottom:2px solid #00B2FF; padding-bottom:5px; display:inline-block; }
          .plaud-doc h3 { font-size:15px; font-weight:700; margin:18px 0 8px; }
          .plaud-doc p  { margin:9px 0; }
          .plaud-doc ul, .plaud-doc ol { margin:9px 0; padding-left:22px; }
          .plaud-doc li { margin:4px 0; }
          .plaud-doc table { border-collapse:collapse; width:100%; margin:14px 0; font-size:13px; }
          .plaud-doc th { background:#f1f5f9; border:1px solid #cbd5e1; padding:8px 10px;
                          text-align:left; font-weight:700; }
          .plaud-doc td { border:1px solid #e2e8f0; padding:8px 10px; }
          .plaud-doc img { max-width:100%; margin:14px 0; }
          .plaud-doc blockquote { border-left:3px solid #00B2FF; margin:12px 0; padding:4px 14px; color:#475569; }
          .plaud-doc strong { font-weight:700; }
          .plaud-doc hr { border:none; border-top:1px solid #e2e8f0; margin:20px 0; }
        `}</style>
      </div>
    </div>
  );
}

export default function PlaudRecordingsPage() {
  const [list, setList]           = useState([]);
  const [filter, setFilter]       = useState('all');
  const [previewRec, setPreviewRec] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get('/plaud').then(r => setList(r.data));
  useEffect(() => { load(); }, []);

  const handleSaved = (meetingNoteId) => {
    setPreviewRec(null);
    load();
    navigate(`/work/meeting/${meetingNoteId}`);
  };

  const reject = async (id) => {
    if (!confirm('이 녹음을 거절하시겠습니까?')) return;
    await api.post(`/plaud/${id}/reject`);
    load();
  };

  const reset  = async (id) => { await api.post(`/plaud/${id}/reset`);   load(); };
  const del    = async (id) => {
    if (!confirm('이 녹음 기록을 삭제하시겠습니까?')) return;
    await api.delete(`/plaud/${id}`);
    load();
  };

  const filtered     = filter === 'all' ? list : list.filter(r => r.status === filter);
  const pendingCount = list.filter(r => r.status === 'pending').length;

  return (
    <div>
      <PageHeader icon={Mic} title="Plaud 녹음 관리">
        <button
          onClick={() => setComposeOpen(true)}
          className="flex items-center gap-2 bg-brand hover:bg-brand/80 text-white rounded-lg px-4 py-2 text-sm font-semibold"
        >
          <Sparkles size={14} />
          붙여넣기로 회의록 만들기
        </button>
      </PageHeader>

      <div className="flex gap-2 mb-4">
        {[
          { key: 'all',      label: '전체' },
          { key: 'pending',  label: `대기중 ${pendingCount > 0 ? `(${pendingCount})` : ''}` },
          { key: 'approved', label: '승인됨' },
          { key: 'rejected', label: '거절됨' },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              filter === f.key ? 'bg-brand text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-600">
          <Mic size={32} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">녹음이 없습니다.</p>
          <p className="text-xs mt-1">Plaud에서 녹음 후 Zapier가 Google Sheets에 자동 전송합니다.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(r => (
            <RecordingCard
              key={r.id}
              rec={r}
              onPreview={setPreviewRec}
              onReject={reject}
              onReset={reset}
              onDelete={del}
            />
          ))}
        </div>
      )}

      {previewRec && (
        <PreviewModal
          rec={previewRec}
          onClose={() => setPreviewRec(null)}
          onSaved={handleSaved}
        />
      )}

      {composeOpen && (
        <ComposeModal
          onClose={() => setComposeOpen(false)}
          onSaved={(noteId) => { setComposeOpen(false); navigate(`/work/meeting/${noteId}`); }}
        />
      )}
    </div>
  );
}
